import { Icon, usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Card, Table } from '@/components_deprecated'
import { isSameDay, parseISO } from 'date-fns'
import { format } from '@/utils/date-fns'
import { Fragment, useState } from 'react'
import {
  enforceHourMinuteString,
  stripSecondsFromDuration,
} from '@/modules/activities'
import { Row } from '@/modules/activities/components/overview/ActivitiesWeekTableRow'
import styled from 'styled-components'
import { overloadColor } from '@/utils/style'
import { AttachToCalendarItemModal } from '../AttachToCalendarItemModal'
import type { WorkPeriodGroupNode, WorkPeriodNode } from '../types.graphql'
import { DietButton } from '../components/DietButton'
import { AddonButton } from '@/modules/activities/components/overview/AddonCell'
import { Button, H3 } from '@/components'
import { ViewWorkPeriodLogsModal } from '../components/view-work-period-logs-modal'
import { useSalaryApproveColumns } from '../hooks/useSalaryApproveColumns'
import { ColumnNames } from '@/modules/dynamic_tables'
import { AddonOverview } from '../AddonOverview'
import { WorkPeriodApprovalBoxes } from './WorkPeriodApprovalBoxes'
import { WorkPeriodTimeBlocks } from './WorkPeriodTimeBlocks'
import { WorkPeriodHoursAndMap } from '../WorkPeriodHoursAndMap'
import { SalarySettingSelect } from '@/components_deprecated/Select/SalarySettingSelect'
import type { SalaryModuleConfiguration } from '../hooks/useSalaryModuleConfiguration'

const CalendarItemMarker = styled.div<{ color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 0.5rem 0;
  padding: 1rem;
  border-radius: 0.5rem;
  background: ${props => overloadColor(props.color ?? props.theme.colors.red8)};
  color: ${props => props.theme.colors.white};
  font-size: 1rem;
  line-height: 1rem;
  font-weight: 500;
  text-align: center;
  text-transform: uppercase;
  width: 100px;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
`

const WorkPeriodContainer = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 1rem 0;
  width: 100%;

  & > h3 {
    margin: 0;
  }

  & > div > span {
    opacity: 0;
  }
  &:hover {
    & > div > span {
      opacity: 1;
    }
  }
`

const WorkPeriodWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;

  p {
    font-weight: 600;
  }

  ${props => props.theme.media.mobile} {
    width: 100%;
    max-width: 100%;
  }
`

const FlexWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0;
  gap: 0.5rem;

  ${props => props.theme.media.mobile} {
    width: 100%;
    flex-direction: column;
    width: 100%;
  }
`

const DetachButton = styled.span`
  height: 2rem;
  width: 2rem;
  margin-top: -0.5rem;
  background: ${props => props.theme.colors.gray8};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  i {
    opacity: 0.5;
  }

  &:hover {
    background: ${props => props.theme.colors.gray10};
    i {
      opacity: 1;
    }
  }
  &:active {
    background: ${props => props.theme.colors.gray9};
  }
`

const AttachButton = styled.span`
  height: 2rem;
  width: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    background: ${props => props.theme.colors.red10};
    color: ${props => props.theme.colors.white};
  }
  &:active {
    background: ${props => props.theme.colors.red11};
  }
`

const WorkPeriodGroupInfo = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-left: 0.5rem;

  & > p {
    margin: 5px 0;
  }
`

const StyledRow = styled(Row)`
  height: 14px;
`

const WorkPeriodTitle = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  width: 100%;

  & > h3 {
    margin: 0;
  }

  ${props => props.theme.media.mobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`

const HeaderColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`

const HeaderTopRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  align-items: center;
  justify-content: space-between;
`

const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
`

const ControlsCard = styled.div`
  display: flex;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.gray9};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  background: ${props => props.theme.colors.white};
`

const TextAndAction = styled.div<{ border?: boolean; disabled?: boolean }>`
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  border-radius: ${props => props.theme.standardBorderRadius};
  border: ${props =>
    props.border ? `1px solid ${props.theme.colors.gray9}` : 'none'};
  background-color: ${props => props.theme.colors.white};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'inherit')};

  ${props =>
    props.disabled &&
    `
    & * {
      pointer-events: none;
      cursor: not-allowed;
    }
  `}

  & > *:first-child {
    border-right: 1px solid ${props => props.theme.colors.gray9};
    margin-right: 1rem;
    padding-right: 1rem;
  }
  & > *:last-child {
    cursor: pointer;
  }
  ${props => props.theme.media.mobile} {
    width: 100%;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`

const ActionWrapper = styled.div`
  color: ${props => props.theme.colors.gray1};
  background-color: ${props => props.theme.colors.gray8};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  font-size: 18px;
  display: flex;
  justify-content: space-between;
  overflow: hidden;

  & > div {
    width: 100%;
    padding: 0.5rem 1rem;
  }
  & > div:hover {
    background-color: ${props => props.theme.colors.gray7};
    cursor: pointer;
  }
`

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
`

const EditedAfterLockNote = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
  color: ${props => props.theme.colors.orange};
  font-size: 0.85rem;
  font-weight: 600;
`

// Aligns the salary-setting combobox with the 44px action buttons by removing
// the combobox wrapper's default bottom margin.
const InputCell = styled.div`
  display: flex;
  align-items: center;

  & .mb-2 {
    margin-bottom: 0;
  }
`

export const WorkPeriodGroupDetails: React.FC<{
  group: WorkPeriodGroupNode
  userId: string
  salaryCalendarSettings: SalaryModuleConfiguration
  // Whether the current user may edit these work periods' salary data
  // (salary setting, diet, addons, calendar attachment). Approval
  // visibility/actions are governed separately by the salary config and
  // per-user approval rules.
  canEdit: boolean
  patchWorkPeriod: (
    workPeriodId: string,
    values: Partial<
      Pick<
        WorkPeriodNode,
        | 'approvedByUser'
        | 'approvedByManager'
        | 'userComment'
        | 'managerComment'
      >
    > &
      Partial<{ calendarItem: string | null; salarySetting: string | null }>
  ) => void
  onAttachModalOpen?: () => void
  onAttachModalClose?: () => void
  onClose?: () => void
  refetch: () => void
}> = ({
  group,
  userId,
  salaryCalendarSettings,
  canEdit,
  patchWorkPeriod,
  onAttachModalOpen,
  onAttachModalClose,
  onClose,
  refetch,
}) => {
  const addPrompt = usePrompt()
  const [showMap, setShowMap] = useState(true)

  const translations = useTranslate({
    duration: 'common.duration',
    seeWork: 'common.see-work',
    overtime: 'common.overtime',
    totalWork: 'common.total-work',
    field: 'common.field',
    amount: 'common.amount',
    workPeriod: 'common.work-period',
    paidPause: 'activities.paid-pause',
    salaryCode: 'common.salary-code',

    noCalendarItemConnected: 'salary.no-calendar-item-connected',

    department: 'common.department',
    facility: 'common.facility',
    addons: 'salary.addons',
    editedAfterLock: 'salary.edited-after-lock',
    editedAfterLockAt: ['salary.edited-after-lock-at', { date: '' }],
    actionLog: 'salary.work-period-log',
  })

  const openLog = (workPeriodId: string) =>
    addPrompt<boolean | null>(resolve => (
      <ViewWorkPeriodLogsModal workPeriodId={workPeriodId} onClose={resolve} />
    ))

  // Respect the same dynamic-table column settings as the approve table/cards.
  const { isEnabled: isColumnEnabled } = useSalaryApproveColumns()
  const showSalaryMode = isColumnEnabled(ColumnNames.SALARY_NAME)
  const showDiet = isColumnEnabled(ColumnNames.DIET)
  const showAddons = isColumnEnabled(ColumnNames.ADDONS)
  const hasControls = showSalaryMode || showDiet || showAddons

  // Mirror the approve view: only surface the approval cards for the modes
  // that are actually configured (driver / manager approve).
  const showApproveWrapper =
    salaryCalendarSettings.driverApprove ||
    salaryCalendarSettings.managerApprove

  const calendarItem = group?.calendarItem

  const GroupPeriodStart = calendarItem
    ? parseISO(calendarItem.start)
    : group?.workPeriods[0]?.datetimeStart
    ? parseISO(group?.workPeriods[0]?.datetimeStart)
    : new Date()
  const GroupPeriodEnd = calendarItem
    ? parseISO(calendarItem.end)
    : group?.workPeriods[0]?.datetimeEnd
    ? parseISO(group?.workPeriods[0]?.datetimeEnd)
    : new Date()
  const groupIsSameDay =
    GroupPeriodStart && GroupPeriodEnd
      ? isSameDay(GroupPeriodStart, GroupPeriodEnd)
      : false
  const groupStartDate = groupIsSameDay
    ? format(GroupPeriodStart, 'd MMM')
    : format(GroupPeriodStart, 'd MMM HH:mm')
  const groupEndDate = groupIsSameDay
    ? format(GroupPeriodEnd, 'd MMM')
    : format(GroupPeriodEnd, 'd MMM HH:mm')

  const WorkPeriodGroupTitle = groupIsSameDay
    ? `${format(GroupPeriodStart, 'd MMM HH:mm')} - ${format(
        GroupPeriodEnd,
        'HH:mm'
      )}`
    : `${groupStartDate} - ${groupEndDate}`

  const firstWorkPeriod = group?.workPeriods?.[0]

  async function handleAttachToCalendarItem(workPeriodId: string) {
    onAttachModalOpen?.()
    const { data } = await addPrompt<{ id: string } | null>(resolve => (
      <AttachToCalendarItemModal
        workPeriodId={workPeriodId}
        onSubmit={resolve}
      />
    ))

    onAttachModalClose?.()
    if (data === null) return
    patchWorkPeriod(workPeriodId, {
      calendarItem: data?.id ?? null,
    })
  }

  const color = calendarItem
    ? group?.workPeriods?.every(
        workPeriod => workPeriod.approvedByUser && workPeriod.approvedByManager
      )
      ? 'green'
      : 'orange'
    : group?.workPeriods[0]?.approvedByUser &&
      group?.workPeriods[0]?.approvedByManager
    ? 'green'
    : 'red8'

  return (
    <FlexWrapper>
      {onClose && (
        <TitleWrapper>
          <FlexRow>
            <ActionWrapper>
              <div onClick={() => onClose()}>
                <Icon icon="arrow-left" onClick={() => onClose()} />
              </div>
            </ActionWrapper>
          </FlexRow>
        </TitleWrapper>
      )}
      <WorkPeriodWrapper>
        {calendarItem && (
          <Card>
            <>
              <TitleWrapper>
                <CalendarItemMarker color={color}>
                  {calendarItem.title}
                </CalendarItemMarker>
                <H3>{WorkPeriodGroupTitle}</H3>
              </TitleWrapper>

              <WorkPeriodGroupInfo>
                <p>
                  <b>{translations.department}</b>:{' '}
                  {calendarItem?.vehicle?.department?.name ?? ''}
                </p>
                <p>
                  <b>{translations.facility}</b>:{' '}
                  {calendarItem?.vehicle?.facility?.name ?? ''}
                </p>
                <br />
                <p>
                  <b>{translations.totalWork}</b>:{' '}
                  {stripSecondsFromDuration(group?.totalWorkTime ?? '')}
                </p>
                <p>
                  <b>{translations.paidPause}</b>: {group?.paidPause ?? ''}
                </p>
                <Table
                  noBorders
                  noShadow
                  headerMargin
                  rowHoverBackground="gray10"
                >
                  <thead>
                    <tr>
                      <th>{translations.salaryCode}</th>
                      <th>{translations.amount}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.groupedActivitySums.map(activitySum => (
                      <StyledRow
                        key={activitySum.id}
                        isActive={true}
                        isAbsence={false}
                        onClick={() => {}}
                      >
                        <td>{activitySum?.name}</td>
                        <td>
                          {activitySum.negative ? '-' : ' '}
                          {enforceHourMinuteString(activitySum?.sum)}
                        </td>
                      </StyledRow>
                    ))}
                  </tbody>
                </Table>
              </WorkPeriodGroupInfo>
            </>
          </Card>
        )}
        {group?.workPeriods?.map((workPeriod, j) => {
          const workPeriodStart = parseISO(workPeriod.datetimeStart)
          const workPeriodEnd = parseISO(workPeriod.datetimeEnd)

          const displayDateHours = `${format(
            workPeriodStart,
            'HH:mm'
          )} - ${format(workPeriodEnd, 'HH:mm')}`

          const displayDateDays = `${format(
            workPeriodStart,
            'd MMM HH:mm'
          )} - ${format(workPeriodEnd, 'd MMM HH:mm')}`

          const title = calendarItem
            ? `${translations.workPeriod} ${j + 1} (${format(
                workPeriodStart,
                'd MMM'
              )}): ${displayDateHours}`
            : `${translations.workPeriod}: ${displayDateDays}`

          return (
            <Fragment key={workPeriod.id}>
              <WorkPeriodContainer>
                <HeaderColumn>
                  <HeaderTopRow>
                    <WorkPeriodTitle>
                      {!calendarItem && canEdit && (
                        <CalendarItemMarker color={color}>
                          <AttachButton
                            onClick={() =>
                              handleAttachToCalendarItem(
                                firstWorkPeriod?.id ?? ''
                              )
                            }
                          >
                            <Icon icon="plus" size="24" />
                          </AttachButton>
                        </CalendarItemMarker>
                      )}
                      <H3>{title}</H3>
                      {calendarItem && canEdit && (
                        <DetachButton
                          onClick={() =>
                            patchWorkPeriod(workPeriod.id, {
                              calendarItem: null,
                            })
                          }
                        >
                          <i className="fa fa-chain-broken" />
                        </DetachButton>
                      )}
                    </WorkPeriodTitle>
                    <HeaderActions>
                      {workPeriod.editedAfterLock && (
                        <EditedAfterLockNote>
                          <Icon icon="exclamation-triangle" color="orange" />
                          {workPeriod.lockedAt
                            ? translations.editedAfterLockAt({
                                date: format(
                                  parseISO(workPeriod.lockedAt),
                                  'dd.MM.yyyy HH:mm'
                                ),
                              })
                            : translations.editedAfterLock}
                        </EditedAfterLockNote>
                      )}
                      <Button
                        size="icon-lg"
                        variant="outline"
                        title={translations.actionLog}
                        aria-label={translations.actionLog}
                        onClick={() => openLog(workPeriod.id)}
                      >
                        <Icon icon="clock-rotate-left" />
                      </Button>
                    </HeaderActions>
                  </HeaderTopRow>
                  {hasControls && (
                    <ControlsCard>
                      <WorkPeriodTitle>
                        {showSalaryMode && (
                          <InputCell>
                            <SalarySettingSelect
                              value={workPeriod.salarySetting?.id || null}
                              disabled={
                                workPeriod.approvedByManager || !canEdit
                              }
                              className="bg-gray-100 border-gray-200"
                              onChange={value =>
                                patchWorkPeriod(workPeriod.id, {
                                  salarySetting: value === '' ? null : value,
                                })
                              }
                            />
                          </InputCell>
                        )}
                        {showDiet && (
                          <DietButton
                            workPeriod={workPeriod}
                            userId={userId}
                            canEdit={!workPeriod.approvedByManager && canEdit}
                            border={true}
                            onChange={refetch}
                          />
                        )}
                        {showAddons && (
                          <TextAndAction
                            border={true}
                            disabled={workPeriod.approvedByManager || !canEdit}
                            onClick={() => {}}
                          >
                            <div>{translations.addons}</div>
                            <AddonButton
                              userId={userId}
                              datetimeStart={workPeriodStart}
                              datetimeEnd={workPeriodEnd}
                              addonsAsText={['']}
                              disabled={
                                workPeriod.approvedByManager || !canEdit
                              }
                            />
                          </TextAndAction>
                        )}
                      </WorkPeriodTitle>
                    </ControlsCard>
                  )}
                </HeaderColumn>
                <WorkPeriodTimeBlocks
                  datetimeStart={workPeriod.datetimeStart}
                  datetimeEnd={workPeriod.datetimeEnd}
                  plannedStart={calendarItem?.start ?? null}
                  plannedEnd={calendarItem?.end ?? null}
                  startLocation={workPeriod.startLocation?.details}
                  endLocation={workPeriod.endLocation?.details}
                  timeEntries={workPeriod.timeEntries}
                />
                {showApproveWrapper && (
                  <WorkPeriodApprovalBoxes
                    workPeriod={workPeriod}
                    patchWorkPeriod={patchWorkPeriod}
                    driverApprove={salaryCalendarSettings.driverApprove}
                    managerApprove={salaryCalendarSettings.managerApprove}
                    onModalOpen={onAttachModalOpen}
                    onModalClose={onAttachModalClose}
                  />
                )}
                <WorkPeriodHoursAndMap
                  workPeriodId={workPeriod.id}
                  activitiesOnly={
                    workPeriod.salarySetting
                      ? workPeriod.salarySetting.salaryType !== 'HOUR' &&
                        (workPeriod.salaryDay?.partOfDay ?? 0) > 0
                      : false
                  }
                  showMap={showMap}
                  onToggleMap={() => setShowMap(value => !value)}
                />
              </WorkPeriodContainer>
              <AddonOverview
                userId={workPeriod.user.id}
                datetimeStart={workPeriodStart}
                datetimeEnd={workPeriodEnd}
              />
            </Fragment>
          )
        })}
      </WorkPeriodWrapper>
    </FlexWrapper>
  )
}
